import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'
import { useReCaptcha } from 'next-recaptcha-v3'
import {
  AddressAutocomplete,
  Button,
  Checkbox,
  Col,
  Fieldset,
  Icon,
  Image,
  Input,
  Modal,
  Row,
  Select,
  Text,
  Textarea
} from '@/client/components'
import { useGetCountries, useMutateContact, useGetStates } from '@/client/hooks'
import { IAddressSuggestion } from '@/client/types'
import { format } from '@/client/utils'
import { assertFields } from './asserts'
import { initialForm, requiredFields } from './form'
import * as S from './styles'
import { ContactFormProps, FormProps } from '../types'

export const ContactForm: FC<ContactFormProps> = ({ contact_form, side_square_box, image, asserts, result }) => {
  const { locale } = useRouter()
  const [form, setForm] = useState<FormProps>(initialForm)
  const [isLoadingForm, setIsLoadingForm] = useState<boolean>(false)
  const [formError, setFormError] = useState<string>('')
  const { executeRecaptcha } = useReCaptcha()
  const { mutate: mutateContact, isLoading, isSuccess } = useMutateContact()
  const { data: countriesData } = useGetCountries(locale)
  const [activeIndex, setActiveIndex] = useState<number>(0)
  const [checked, setChecked] = useState<boolean>(false)
  const [isClosing, setIsClosing] = useState<boolean>(false)
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [countryCode, setCountryCode] = useState<string>('')
  const [defaultCountry, setDefaultCountry] = useState<number>(0)
  const { data: statesData } = useGetStates(String(defaultCountry))

  useEffect(() => {
    fetch(`${process.env.NEXT_PUBLIC_GEOLOGICAL_COUNTRY}`)
      .then((response) => response.json())
      .then((data) => {
        setCountryCode(data.country_code)
      })
  }, [])

  useEffect(() => {
    if (!countriesData?.length) return
    const matchedCountry = countriesData.find((country) => country.landCode === countryCode)
    const countryId = matchedCountry?.id ?? countriesData[0].id
    setDefaultCountry(countryId)
    // Set country
    handleChange({
      target: {
        name: 'CountryId',
        value: String(countryId)
      }
    })
  }, [countryCode, countriesData])

  const handleBoxClick = (index: number) => {
    setActiveIndex(index === activeIndex ? 0 : index)
  }

  const selectOptions = useMemo(
    () => ({
      countries: countriesData || [],
      states: statesData || []
    }),
    [countriesData, locale, statesData]
  )

  const handleValidateForm = useCallback(() => {
    const validation = requiredFields.every((field) => !form[field].hasError) && checked
    return !validation
  }, [form])

  const handleChange = useCallback(
    (event) => {
      const { type, target } = event
      const { name } = target
      const value = name === 'Postcode' && type === 'blur' ? format.postcode(target.value) : target.value

      if (name === 'CountryId') {
        setDefaultCountry(event.target.value)
      }

      const error = assertFields(name, value, asserts)

      setForm((oldForm) => {
        let newValue = value

        if (event?.target?.type === 'checkbox') {
          if (checked) {
            newValue = '0'
            setChecked(false)
          } else {
            newValue = '1'
            setChecked(true)
          }
        }

        return {
          ...oldForm,
          [name]: {
            value: newValue,
            dirty: oldForm[name].dirty || type === 'blur',
            error,
            hasError: Boolean(error)
          }
        }
      })
    },
    [checked]
  )

  /**
   * A suggestion already carries the parsed address, so all three fields are
   * filled in a single update that runs the same validation as typing.
   */
  const handleSelectAddress = useCallback(
    (suggestion: IAddressSuggestion) => {
      setCountryCode(suggestion.countryCode)

      const matchedState = statesData?.find((state) => state.name === suggestion.state)
      const stateId = matchedState?.id ?? statesData?.[0]?.id

      const filledFields = {
        Address: suggestion.address,
        Postcode: format.postcode(suggestion.postcode),
        Town: suggestion.town,
        StateId: stateId
      }

      setForm((oldForm) => {
        const newForm = { ...oldForm }

        Object.entries(filledFields).forEach(([name, value]) => {
          if (!value) return
          const error = assertFields(name, value, asserts)
          newForm[name] = { value, dirty: true, error, hasError: Boolean(error) }
        })

        return newForm
      })
    },
    [asserts, statesData, countryCode]
  )

  const handleReloadForm = useCallback(() => {
    setForm(initialForm)
    setChecked(false)
    setDefaultCountry(0)
  }, [initialForm])

  const handleSubmitForm = useCallback(async () => {
    const token = await executeRecaptcha('contact_form_submit')
    const data = {}
    Object.entries(form).map((key) => {
      data[key[0]] = key[1].value
    })

    data['CountryId'] === '' && (data['CountryId'] = defaultCountry.toString())
    data['g_recaptcha_token'] = token
    data['lang'] = process.env.NEXT_PUBLIC_DEFAULT_LOCALE === 'uk' ? 'en' : process.env.NEXT_PUBLIC_DEFAULT_LOCALE
    mutateContact(data)
    setIsLoadingForm(isLoading)
    setIsModalOpen(true)
    setForm(initialForm)
    setChecked(false)
    setDefaultCountry(0)

    if (!isLoading) {
      const state = !isSuccess ? result.success_text : result.error_text
      setFormError(state)
    }
  }, [executeRecaptcha, form, isLoading, isSuccess, mutateContact, initialForm])

  const closeModalWithDelay = useCallback(() => {
    setIsClosing(true)
    setTimeout(() => {
      setIsModalOpen(false)
      setIsClosing(false)
    }, 1000)
  }, [])

  const directions = [
    { dir: 'column', mq: 'sm' },
    { dir: 'row', mq: 'lg' }
  ]

  return (
    <>
      <Row directions={directions}>
        <Col>
          <S.ContactForm>
            <Text size='md' weight='bold' margin='0'>
              {contact_form.subheading}
            </Text>
            <Text size='md' weight='normal' margin='0'>
              {contact_form.text}
            </Text>
            <Fieldset kind='row' newVersion>
              <></>
              <S.FieldList fullWidth>
                <Select
                  isBlock
                  required
                  id='c_reason'
                  name='c_reason'
                  placeholder={contact_form.subject.subject_title}
                  value={form?.c_reason?.value ?? ''}
                  options={contact_form.subject.subject_options}
                  onChange={handleChange}
                  onBlur={handleChange}
                  hasError={form?.c_reason.hasError && form?.c_reason.dirty}
                  error={form?.c_reason.error}
                />
              </S.FieldList>
            </Fieldset>

            <Fieldset kind='row' newVersion>
              <Text size='md' weight='normal' margin='0'>
                {contact_form?.personal_informations?.title}
              </Text>
              <S.FieldList>
                <Input
                  isBlock
                  dimension='small'
                  type='text'
                  id='FirstName'
                  name='FirstName'
                  placeholder={contact_form?.personal_informations?.first_name}
                  value={form?.FirstName?.value ?? ''}
                  onChange={handleChange}
                  onBlur={handleChange}
                  hasError={form?.FirstName.hasError && form?.FirstName.dirty}
                  error={form?.FirstName.error}
                />

                <Input
                  isBlock
                  dimension='small'
                  type='text'
                  id='Surname'
                  name='Surname'
                  placeholder={contact_form?.personal_informations?.last_name}
                  value={form?.Surname?.value ?? ''}
                  onChange={handleChange}
                  onBlur={handleChange}
                  hasError={form?.Surname.hasError && form?.Surname.dirty}
                  error={form?.Surname.error}
                />
              </S.FieldList>
            </Fieldset>

            <Fieldset kind='row' newVersion>
              <Text size='md' weight='normal' margin='0'>
                {contact_form?.company?.title}
              </Text>
              <S.FieldList>
                <Input
                  isBlock
                  dimension='small'
                  type='text'
                  id='Company'
                  name='Company'
                  placeholder={contact_form.company.company_name}
                  value={form?.Company?.value ?? ''}
                  onChange={handleChange}
                  onBlur={handleChange}
                  hasError={form?.Company.hasError && form?.Company.dirty}
                  error={form?.Company.error}
                />
                <Input
                  isBlock
                  dimension='small'
                  type='text'
                  id='TradeName'
                  name='TradeName'
                  placeholder={contact_form.company.trade_name}
                  value={form.TradeName.value ?? ''}
                  onChange={handleChange}
                  onBlur={handleChange}
                  hasError={form.TradeName.hasError && form.TradeName.dirty}
                  error={form.TradeName.error}
                />
                <AddressAutocomplete
                  isBlock
                  dimension='small'
                  type='text'
                  id='Address'
                  name='Address'
                  placeholder={contact_form.company.address}
                  value={form.Address.value ?? ''}
                  onChange={handleChange}
                  onBlur={handleChange}
                  onSelect={handleSelectAddress}
                  hasError={form.Address.hasError && form.Address.dirty}
                  error={form.Address.error}
                />
                <Input
                  isBlock
                  dimension='small'
                  type='text'
                  id='Postcode'
                  name='Postcode'
                  placeholder={contact_form.company.postcode}
                  value={form.Postcode.value ?? ''}
                  onChange={handleChange}
                  onBlur={handleChange}
                  hasError={form.Postcode.hasError && form.Postcode.dirty}
                  error={form.Postcode.error}
                />
                <Input
                  isBlock
                  dimension='small'
                  type='text'
                  id='Town'
                  name='Town'
                  placeholder={contact_form?.company?.city}
                  value={form?.Town?.value ?? ''}
                  onChange={handleChange}
                  onBlur={handleChange}
                  hasError={form?.Town.hasError && form?.Town.dirty}
                  error={form?.Town.error}
                />
                <Select
                  isBlock
                  required
                  id='CountryId'
                  name='CountryId'
                  placeholder={contact_form?.company?.country}
                  value={defaultCountry}
                  options={selectOptions.countries}
                  onChange={handleChange}
                  onBlur={handleChange}
                  hasError={form?.CountryId.hasError && form?.CountryId.dirty}
                  error={form?.CountryId.error}
                />
              </S.FieldList>
            </Fieldset>
            <Fieldset kind='row' newVersion className='noLegend'>
              <></>
              <S.FieldList fullWidth>
                {locale === 'au' && selectOptions.states.length > 0 && (
                  <Select
                    isBlock
                    required
                    id='StateId'
                    name='StateId'
                    placeholder={'Please select a state / territory'}
                    value={form?.StateId?.value ?? ''}
                    options={selectOptions.states}
                    onChange={handleChange}
                    onBlur={handleChange}
                    hasError={form?.StateId.hasError && form?.StateId.dirty}
                    error={form?.StateId.error}
                  />
                )}
              </S.FieldList>
            </Fieldset>
            <Fieldset kind='row' newVersion>
              <Text size='md' weight='normal' margin='0'>
                {contact_form?.contact?.title}
              </Text>
              <S.FieldList>
                <Input
                  isBlock
                  dimension='small'
                  type='text'
                  id='Telephone'
                  name='Telephone'
                  placeholder={contact_form?.contact?.telephone}
                  value={form?.Telephone?.value ?? ''}
                  onChange={handleChange}
                  onBlur={handleChange}
                  hasError={form?.Telephone.hasError && form?.Telephone.dirty}
                  error={form?.Telephone.error}
                />

                <Input
                  isBlock
                  dimension='small'
                  type='text'
                  id='Mobile'
                  name='Mobile'
                  placeholder={contact_form?.contact?.mobile}
                  value={form?.Mobile?.value ?? ''}
                  onChange={handleChange}
                  onBlur={handleChange}
                />

                <Input
                  isBlock
                  dimension='small'
                  type='text'
                  id='Email'
                  name='Email'
                  placeholder={contact_form?.contact?.email}
                  value={form?.Email?.value ?? ''}
                  onChange={handleChange}
                  onBlur={handleChange}
                  hasError={form?.Email.hasError && form?.Email.dirty}
                  error={form?.Email.error}
                />
              </S.FieldList>
            </Fieldset>

            <Fieldset kind='row' newVersion>
              <Text size='md' weight='normal' margin='0'>
                {contact_form.message}
              </Text>

              <S.FieldList fullWidth>
                <Textarea
                  isBlock
                  dimension='large'
                  id='Text'
                  name='Text'
                  value={form?.Text?.value ?? ''}
                  onChange={handleChange}
                  onBlur={handleChange}
                  hasError={form?.Text.hasError && form?.Text.dirty}
                  error={form?.Text.error}
                />
              </S.FieldList>
            </Fieldset>

            <Fieldset kind='row' newVersion>
              <></>

              <S.FieldList fullWidth>
                <Checkbox
                  id='agb_accepted'
                  name='agb_accepted'
                  value='0'
                  label={contact_form.checkbox_text}
                  checked={checked}
                  kind='square'
                  onChange={handleChange}
                />
              </S.FieldList>
            </Fieldset>

            <S.FieldsetActions>
              <Button id='refreshButton' kind='white' onClick={handleReloadForm}>
                <Icon name='reload' size='s5' />
                {contact_form.refresh_button}
              </Button>
              <Button
                kind='warning'
                isLoading={isLoadingForm}
                isDisabled={handleValidateForm()}
                onClick={handleSubmitForm}
                style={{ borderRadius: '2rem' }}
              >
                <Icon name='message' />
                {contact_form.submit_button}
              </Button>
            </S.FieldsetActions>

            {formError && (
              <>
                {isModalOpen && (
                  <div className={`modal ${isClosing ? 'modal-hidden' : 'modal-open'}`}>
                    <Modal
                      size='medium'
                      infos={{
                        subtitle: formError
                      }}
                      close={closeModalWithDelay}
                      func={closeModalWithDelay}
                      cancelButtonName={''}
                      className={'background'}
                      overflow={false}
                    ></Modal>
                  </div>
                )}
              </>
            )}
          </S.ContactForm>
        </Col>
        <Col>
          <S.SecondColumn>
            <S.SideElement>
              {side_square_box?.map(({ key, icon_name, side_subtitle, side_text, extra_text, href_string }) => (
                <S.SideBox
                  as={extra_text ? 'a' : 'div'}
                  key={key}
                  isActive={key === activeIndex}
                  onMouseEnter={() => handleBoxClick(key)}
                  onMouseLeave={() => setActiveIndex(0)}
                  href={href_string}
                  style={{ cursor: extra_text ? 'pointer' : 'default', textDecoration: 'none' }}
                >
                  <Icon name={icon_name} size='s7' />
                  <Text size='md' color='weak' weight='bold' margin='0'>
                    {side_subtitle}
                  </Text>
                  <Text size='md' color='weak' weight='normal' margin='0' lineHeight='1.4'>
                    {side_text}
                  </Text>
                  <Text size='md' weight='bold' margin='0' color='primary'>
                    {extra_text}
                  </Text>
                </S.SideBox>
              ))}
            </S.SideElement>

            <S.ImageAligner>
              <Image src={image.url} alt={image.alt} width='640' height='650' />
            </S.ImageAligner>
          </S.SecondColumn>
        </Col>
      </Row>
    </>
  )
}
